// postMessage the document height to the parent frame whenever the body
// resizes. The Rumio admin shell listens for these messages and adjusts
// the iframe height so the embed feels seamless rather than scrolling
// inside its own viewport.
//
// Protocol (apps contract §3.4): `{ type: 'rumio:height', height: <px> }`.
// Keep that shape exact — the host ignores anything else (this used to send
// `rumio:iframe:resize`, which the host never listened for).

const MESSAGE_TYPE = 'rumio:height';

export function startIframeResizeReporter(): () => void {
  if (typeof window === 'undefined' || window.parent === window) {
    // Not embedded — no-op.
    return () => {};
  }

  let lastReported = -1;
  function report(): void {
    const height = document.documentElement.scrollHeight;
    if (height === lastReported) return;
    lastReported = height;
    try {
      window.parent.postMessage({ type: MESSAGE_TYPE, height }, '*');
    } catch {
      // ignore — cross-origin restrictions handled by the parent.
    }
  }

  // Initial report after a tick so React has flushed the first paint.
  const initial = setTimeout(report, 0);

  const observer = new ResizeObserver(report);
  observer.observe(document.body);

  // Some content (route changes, async data) doesn't trigger ResizeObserver
  // because nothing actually re-flows; a low-frequency interval covers this
  // without burning the main thread.
  const interval = window.setInterval(report, 1000);

  return () => {
    clearTimeout(initial);
    observer.disconnect();
    window.clearInterval(interval);
  };
}
